import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ImageToggleComponent } from '@app/components/forms/image-toggle/image-toggle.component';
import { CompanySSO, SSOProvider, SSO_PROVIDERS } from '@app/models/company/settings/authentication/company-sso.model';
import { PlatformModule } from '@app/platform.module';
import { NotifyService } from '@app/services';
import OptionalObservable, { waitForOptionalObservable } from '@app/types/optional-observable';
import { TranslatableKey } from '@app/types/translatable.type';
import { TranslateModule } from '@ngx-translate/core';
import { keyBy, mapValues } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type SSOProviderConfig = { translationKey: TranslatableKey; logoURL: string };

type LoadingLevel = 'global' | SSOProvider | null;

@Component({
    selector: 'app-sign-in-sso-setting',
    templateUrl: './sso.template.html',
    styleUrls: ['./sso.style.scss'],
    standalone: true,
    imports: [CommonModule, PlatformModule, ImageToggleComponent, TranslateModule, FormsModule],
})
export class SignInSSOSetting implements OnDestroy, OnInit {
    @Input() companySSOSetting: OptionalObservable<CompanySSO | undefined>;

    loadingLevel: LoadingLevel = null;
    isErrored = false;

    // For convenience we store these values in a mapped object rather than an array. It's easier to find values by their key rather than iterating through the array every time.
    activeSSOProviders = mapValues(keyBy(SSO_PROVIDERS), () => false) as Record<SSOProvider, boolean>;

    // Unfortunately a Map is required here in order to keep type-safety in the template when using the keyvalue pipe
    readonly SSO_PROVIDER_CONFIG: Map<SSOProvider, SSOProviderConfig> = new Map([
        [
            'google',
            {
                translationKey: 'settings.signIn.sso.providers.GOOGLE',
                logoURL: 'assets/img/brands/google-transparent.png',
            },
        ],
        [
            'microsoft',
            {
                translationKey: 'settings.signIn.sso.providers.MICROSOFT',
                logoURL: 'assets/img/brands/microsoft-transparent.png',
            },
        ],
        [
            'okta',
            {
                translationKey: 'settings.signIn.sso.providers.OKTA',
                logoURL: 'assets/img/brands/brand_okta.svg',
            },
        ],
        // ! PDS-1717: Disabling azure and okta since they're not currently available for use within the mobile app
        // [
        //     'azure',
        //     {
        //         translationKey: 'settings.signIn.sso.providers.AZURE',
        //         logoURL: 'assets/img/brands/brand_azure.svg',
        //     },
        // ],
    ]);

    private _isExpanded = false; // Used for convenience to not have to constantly check if individual providers are toggled or not
    private _companySSOSetting?: CompanySSO;
    private _onAPIError$ = new Subject<{ providers: SSOProvider[]; isEnabled: boolean }>();
    private _onDestroy$ = new Subject<boolean>();

    constructor(private notifyService: NotifyService) {
        this._onAPIError$.pipe(takeUntil(this._onDestroy$)).subscribe(({ providers, isEnabled }) => {
            // flip back each toggle
            providers.forEach((provider) => {
                this.activeSSOProviders[provider] = !isEnabled;
            });
            // Flip back expander toggle
            if (providers.length) {
                this._isExpanded = true;
            }
        });
    }

    get isExpanded(): boolean {
        return this._isExpanded;
    }

    set isExpanded(val: boolean) {
        this._isExpanded = val;
        // If the setting update fails to save, we want a reference to the currently enabled providers so that we can flip them back
        const currentlyEnabled = this.activeSSOProviderArray;
        this.activeSSOProviders = mapValues(this.activeSSOProviders, () => val);
        if (!this._isExpanded) {
            this.handleProviderToggle(currentlyEnabled, false);
        } else {
            this.handleProviderToggle([...SSO_PROVIDERS], true);
        }
    }

    ngOnInit(): void {
        waitForOptionalObservable(this.companySSOSetting, this.setCompanySSOSetting.bind(this)).catch(
            () => (this.isErrored = true)
        );
    }

    ngOnDestroy(): void {
        this._onDestroy$.next();
        this._onDestroy$.complete();
    }

    setCompanySSOSetting(companySSOSetting?: CompanySSO): void {
        if (!companySSOSetting) {
            // If it's no longer loading, and the setting is still null, we need to mark the component as errored
            this.isErrored = true;
            return;
        }

        this._companySSOSetting = companySSOSetting;

        // Toggle each of the individual providers that are returned from the backend
        companySSOSetting.value.forEach((enabledProvider) => {
            this.activeSSOProviders[enabledProvider] = true;
            this._isExpanded = true;
        });
    }

    handleProviderToggle(providers: SSOProvider[], isEnabled: boolean): void {
        // If we're flipping all of the switches (on or off) then we set the loading level to be the entire component
        if (providers.length > 1 || this.activeSSOProviderArray.length === 0) {
            this.loadingLevel = 'global';
        } else {
            // Set the loading state on individual cards
            this.loadingLevel = providers[0];
        }

        this._companySSOSetting
            ?.setEnabledProviders(this.activeSSOProviderArray)
            ?.catch(() => {
                this.notifyService.error('settings.sso.updatingFailedErrorMessage');
                // Model updates have to be reversed AFTER this function execution completes
                this._onAPIError$.next({ providers, isEnabled });
            })
            .finally(() => {
                this.loadingLevel = null;
                // Check whether any toggles are flipped in order to flip global toggle
                this._isExpanded = this.activeSSOProviderArray.length > 0;
            });
    }

    // We need to be able to convert the mapped object back into an array in order to send it to the backend for saving
    private get activeSSOProviderArray(): SSOProvider[] {
        return Object.entries(this.activeSSOProviders)
            .filter(([, value]) => value)
            .map(([key]) => key as SSOProvider);
    }
}
